import type { ApiDashboardElement } from '@highimpact-frontend/api-types';

import type { DashboardWidgetInstance } from '../state/dashboardRuntimeTypes';
import type { DashboardStoreType } from '../state/dashboard/initials';
import type { WidgetType } from '../state/dashboard/widget';
import {
  findGridIdAndTypeByElementId,
  getDashboardAtKey,
} from './gridLookup';

/** Reads a dashboard element from a runtime/dashboard store snapshot. */
export const readElementFromSlice = (
  dashboardSlice: DashboardStoreType,
  dashboardElementId: number
): ApiDashboardElement | undefined => {
  const gridData = findGridIdAndTypeByElementId(
    dashboardElementId,
    dashboardSlice
  );
  if (!gridData) {
    return undefined;
  }

  const dashboard = getDashboardAtKey(
    gridData.id,
    gridData.type,
    dashboardSlice
  );
  if (!dashboard) {
    return undefined;
  }

  const widget = Object.values(dashboard.widgets).find(
    (entry) => entry.dashboardElementId === dashboardElementId
  );

  return widget?.elementData;
};

/** Reads a widget instance from a runtime/dashboard store snapshot. */
export const readWidgetFromSlice = (
  dashboardSlice: DashboardStoreType,
  widgetId: string
): DashboardWidgetInstance | undefined => {
  const fromGrids = Object.values(dashboardSlice.grids ?? {})
    .map((grid) => grid.widgets[widgetId])
    .find(Boolean) as WidgetType | undefined;

  const fromReports = Object.values(dashboardSlice.reports ?? {})
    .map((grid) => grid.widgets[widgetId])
    .find(Boolean) as WidgetType | undefined;

  const widget = fromGrids ?? fromReports;
  if (!widget) {
    return undefined;
  }

  return {
    widgetId,
    widgetType: widget.type,
    dashboardElementId: widget.dashboardElementId,
    elementData: widget.elementData,
  };
};
